import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Loader, fmtDate } from "@/components/shared";
import { bandColor } from "./Reports";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, ArrowRight, GitCompare, Minus, TrendingUp, TrendingDown } from "lucide-react";

function Delta({ value }) {
  if (value === 0 || value == null) return <span className="inline-flex items-center gap-1 text-muted-foreground font-mono text-xs"><Minus className="h-3 w-3" aria-hidden="true" />0</span>;
  const up = value > 0;
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-xs font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`}>
      {up ? <TrendingUp className="h-3 w-3" aria-hidden="true" /> : <TrendingDown className="h-3 w-3" aria-hidden="true" />}
      {up ? "+" : ""}{value}
    </span>
  );
}

export default function ComparePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [va, setVa] = useState(null);
  const [vb, setVb] = useState(null);
  const [subA, setSubA] = useState(null);
  const [subB, setSubB] = useState(null);

  useEffect(() => {
    api.get(`/assessments/${id}`).then((r) => {
      setPayload(r.data);
      const subs = r.data.submissions;
      if (subs.length >= 2) { setVb(subs[0].id); setVa(subs[1].id); }
      else if (subs.length === 1) { setVa(subs[0].id); setVb(subs[0].id); }
    }).catch(() => setPayload(false));
  }, [id]);

  useEffect(() => { if (va) api.get(`/assessments/submissions/${va}`).then((r) => setSubA(r.data)); }, [va]);
  useEffect(() => { if (vb) api.get(`/assessments/submissions/${vb}`).then((r) => setSubB(r.data)); }, [vb]);

  const rows = useMemo(() => {
    if (!subA || !subB) return [];
    const mapA = {}; (subA.responses || []).forEach((r) => { mapA[r.question_id] = r; });
    const mapB = {}; (subB.responses || []).forEach((r) => { mapB[r.question_id] = r; });
    const keys = Array.from(new Set([...Object.keys(mapA), ...Object.keys(mapB)]));
    return keys.map((k) => {
      const a = mapA[k] || {}; const b = mapB[k] || {};
      return {
        key: k, pillar: a.pillar_name || b.pillar_name, question: a.question_text || b.question_text,
        a: a.score ?? "—", b: b.score ?? "—",
        delta: (typeof a.score === "number" && typeof b.score === "number") ? b.score - a.score : null,
        noteChanged: (a.note || "") !== (b.note || ""),
      };
    });
  }, [subA, subB]);

  if (payload === null) return <Loader label="Loading versions" />;
  if (payload === false) return <div className="text-sm text-muted-foreground">Not available.</div>;

  const { submissions } = payload;
  const overallDelta = subA && subB ? +(subB.scores.overall - subA.scores.overall).toFixed(1) : null;

  return (
    <div>
      <PageHeader
        overline="Version comparison"
        title={payload.assessment.title}
        description="Compare two immutable submission versions side by side."
        actions={<Button variant="outline" onClick={() => navigate(-1)} className="rounded-md"><ArrowLeft className="h-4 w-4 mr-1.5" aria-hidden="true" /> Back</Button>}
      />

      {submissions.length < 2 ? (
        <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          At least two submitted versions are needed to compare.
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-3 items-center mb-6">
            <div className="bg-card border border-border rounded-md p-4">
              <div className="overline mb-2">Baseline (A)</div>
              <Select value={va || ""} onValueChange={setVa}>
                <SelectTrigger className="rounded-md" data-testid="compare-select-a"><SelectValue /></SelectTrigger>
                <SelectContent>{submissions.map((s) => <SelectItem key={s.id} value={s.id}>Version v{s.version} · {fmtDate(s.submitted_at)}</SelectItem>)}</SelectContent>
              </Select>
              {subA && <div className="mt-3 font-heading text-2xl font-bold">{subA.scores.overall}<span className="text-sm text-muted-foreground"> / 100</span> <span className={`text-sm ml-1 ${bandColor(subA.scores.band)}`}>{subA.scores.band}</span></div>}
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground mx-auto hidden sm:block" aria-hidden="true" />
            <div className="bg-card border border-border rounded-md p-4">
              <div className="overline mb-2">Compared (B)</div>
              <Select value={vb || ""} onValueChange={setVb}>
                <SelectTrigger className="rounded-md" data-testid="compare-select-b"><SelectValue /></SelectTrigger>
                <SelectContent>{submissions.map((s) => <SelectItem key={s.id} value={s.id}>Version v{s.version} · {fmtDate(s.submitted_at)}</SelectItem>)}</SelectContent>
              </Select>
              {subB && <div className="mt-3 font-heading text-2xl font-bold">{subB.scores.overall}<span className="text-sm text-muted-foreground"> / 100</span> <span className={`text-sm ml-1 ${bandColor(subB.scores.band)}`}>{subB.scores.band}</span> <span className="ml-2"><Delta value={overallDelta} /></span></div>}
            </div>
          </div>

          <div className="bg-card border border-border rounded-md overflow-hidden" data-testid="compare-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="text-xs uppercase tracking-wider">Pillar</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Question</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-center">A</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-center">B</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-center">Δ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.key} className={`border-b border-border/60 ${r.delta ? "bg-amber-50/40" : ""}`} data-testid={`compare-row-${r.key}`}>
                    <TableCell className="py-2.5 text-xs text-muted-foreground">{r.pillar}</TableCell>
                    <TableCell className="py-2.5 text-sm">{r.question}{r.noteChanged && <span className="ml-2 text-[10px] uppercase tracking-widest font-mono text-amber-600">note changed</span>}</TableCell>
                    <TableCell className="py-2.5 text-center font-mono">{r.a}</TableCell>
                    <TableCell className="py-2.5 text-center font-mono font-semibold">{r.b}</TableCell>
                    <TableCell className="py-2.5 text-center"><Delta value={r.delta} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
